import { apiGet, apiPost } from './client.js';

export type InventoryDuplicateCard = {
  id: string;
  name: string;
  sku: string | null;
  upc: string | null;
  manufacturerPartNumber: string | null;
  condition: string | null;
  costPrice: string;
  retailPrice: string;
  minimumPrice: string | null;
  category: string;
  description: string | null;
  color: string | null;
  taxable: boolean;
  qtyOnHand: string;
  createdAt: string;
  suppliers: Array<{
    supplierId: string;
    supplierName: string;
    supplierSku: string | null;
    costPrice: string;
    isPreferred: boolean;
    lastOrderedAt: string | null;
  }>;
  suggestedSupplier: { supplierId: string; supplierName: string } | null;
};

export type InventoryDuplicateGroup = {
  groupId: string;
  strategy: string;
  confidence: 'high' | 'medium';
  matchedOn: string;
  gradeWarning: boolean;
  gradeReasons: string[];
  items: InventoryDuplicateCard[];
};

export type MergePreview = {
  primaryId: string;
  items: Array<{
    id: string;
    name: string;
    sku: string | null;
    category: string;
    condition: string | null;
    retailPrice: string;
    minimumPrice: string | null;
    description: string | null;
    upc: string | null;
    manufacturerPartNumber: string | null;
    color: string | null;
    taxable: boolean;
    costPrice: string;
    qtyOnHand: string;
    createdAt: string;
  }>;
  gradeWarning: boolean;
  gradeReasons: string[];
  conditionsDiffer: boolean;
  combinedQty: string;
  weightedAvgCost: string | null;
  layers: Array<{
    id: string;
    itemName: string;
    unitCost: string;
    qtyRemaining: string;
    receivedAt: string;
  }>;
  compatibleModels: Array<{ modelId: string; modelName?: string }>;
  ticketCount: number;
  poCount: number;
};

export type SupplierAssignment = {
  supplierId: string;
  supplierSku?: string | undefined;
  costPrice?: number | undefined;
  isPreferred?: boolean | undefined;
};

export function listInventoryDuplicates(params?: { page?: number; pageSize?: number }) {
  return apiGet<InventoryDuplicateGroup[], { page: number; pageSize: number; total: number }>(
    '/api/v1/inventory/items/duplicates',
    { params },
  );
}

export function previewInventoryMerge(body: {
  primaryId: string;
  duplicateIds: string[];
  fieldSelections?: Record<string, string>;
}) {
  return apiPost<MergePreview>('/api/v1/inventory/items/merge/preview', body).then((r) => r.data);
}

export function mergeInventoryItems(body: {
  primaryId: string;
  duplicateIds: string[];
  fieldSelections: Record<string, string>;
  supplierSkuHandling: 'keep_all' | 'manual';
  supplierSkuAssignments: SupplierAssignment[];
  reason: string;
  managerOverrideAuthorizedBy: string;
  acknowledgeGradeWarning?: boolean | undefined;
  acknowledgeConditionMismatch?: boolean | undefined;
}) {
  return apiPost<{ batchId: string; primaryId: string }>('/api/v1/inventory/items/merge', body).then((r) => r.data);
}

export function listInventoryMerges() {
  return apiGet<
    Array<{
      id: string;
      absorbedCount: number;
      combinedQty: string | null;
      reason: string | null;
      performedAt: string;
      reversedAt: string | null;
      primaryId: string | null;
      names: string[];
    }>
  >('/api/v1/inventory/items/merges').then((r) => r.data);
}

export function undoInventoryMerge(id: string, managerOverrideAuthorizedBy: string) {
  return apiPost<{ primaryId: string }>(`/api/v1/inventory/items/merges/${id}/undo`, {
    managerOverrideAuthorizedBy,
  }).then((r) => r.data);
}
